"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
import { MdArrowBack, MdErrorOutline, MdHome } from "react-icons/md";
import { PageTypeAdmin, PageTypePortal } from "@/lib/types";
import type { AppUser } from "@/lib/types/next-auth";
import { getDashboardRouteForUser } from "@/lib/utils";
import { hasAnyAdminRole } from "@/lib/utils/roles";
import { Button, Heading, Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PublicLayout } from "@/components/layout";

interface NotFoundClientProps {
  user: AppUser | null;
}

export function NotFoundClient({ user }: NotFoundClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const theme = useThemeTokens();

  const isTechUser = hasAnyAdminRole(user);

  const isLoggedIn = useMemo(() => {
    return !!user;
  }, [user]);

  const dashboardRoute = useMemo(() => getDashboardRouteForUser(user), [user]);

  const loginRoute = useMemo(() => {
    if (user) {
      return isTechUser ? PageTypeAdmin.SignIn : PageTypePortal.Login;
    }
    if (pathname?.startsWith("/admin")) {
      return PageTypeAdmin.SignIn;
    }
    return PageTypePortal.Login;
  }, [user, isTechUser, pathname]);

  const errorMessage = useMemo(() => {
    if (!isTechUser) {
      return "Honestly, we're as surprised as you are. This page had one job.";
    }

    return "This page doesn't exist or is no longer available. If you followed a link from inside the dashboard, it may need to be updated.";
  }, [isTechUser]);

  const handleGoToLogin = useCallback(() => {
    router.push(loginRoute);
  }, [router, loginRoute]);

  const handleGoToDashboard = useCallback(() => {
    router.push(dashboardRoute);
  }, [router, dashboardRoute]);

  const handleGoBack = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <PublicLayout hideNavbarLinks={true}>
      <Flex
        w="100%"
        py="40px"
        align="center"
        justify="center"
        textAlign="center"
        direction="column"
      >
        <Box mb="24px">
          <Icon
            w="120px"
            h="120px"
            mb="16px"
            color={theme.accent.primary}
            as={MdErrorOutline}
          />
        </Box>

        <Box me="0">
          <Heading
            mb="16px"
            lineHeight="1.2"
            variant="primary"
            fontWeight="bold"
            fontSize={{ base: "48px", md: "72px" }}
          >
            404
          </Heading>
          <Heading
            variant="primary"
            fontSize={{ base: "30px", md: "36px" }}
            mb="10px"
          >
            Page Not Found
          </Heading>
          <Text
            mb="36px"
            maxW="436px"
            fontSize="md"
            mx="0"
            variant="tertiary"
            fontWeight="400"
          >
            {errorMessage}
          </Text>
        </Box>
        <Flex
          gap="16px"
          direction={{ base: "column", sm: "row" }}
          align="center"
        >
          <Button
            h="44px"
            fontSize="sm"
            w="max-content"
            variant="outline"
            fontWeight="500"
            onClick={handleGoBack}
            leftIcon={<Icon as={MdArrowBack} w="20px" h="20px" />}
          >
            Go Back
          </Button>
          {!isLoggedIn ? (
            <Button
              h="44px"
              fontSize="sm"
              w="max-content"
              variant="primary"
              fontWeight="500"
              onClick={handleGoToLogin}
              leftIcon={<Icon as={MdHome} w="20px" h="20px" />}
            >
              Return to Login
            </Button>
          ) : (
            <Button
              h="44px"
              fontSize="sm"
              w="max-content"
              variant="primary"
              fontWeight="500"
              onClick={handleGoToDashboard}
              leftIcon={<Icon as={MdHome} w="20px" h="20px" />}
            >
              Go to Dashboard
            </Button>
          )}
        </Flex>
      </Flex>
    </PublicLayout>
  );
}
